import { fileCategory } from './file-icon';
import type { DatedAttachment } from './room-attachments';

/** 소비자가 두 모듈을 각각 import 하지 않도록 재수출(타입 SSOT 는 room-attachments). */
export type { DatedAttachment };

/**
 * 채팅 첨부 일자 그룹·정렬·검색 (chat-media-date-groups — 2026-08-07).
 *
 * 우측 "대화상대" 패널의 미디어 섹션이 최신순 평면 나열뿐이라, 첨부가 쌓이면 과거 파일을 찾을 방법이
 * 스크롤밖에 없었다. 이 모듈은 방 전체 첨부를 **로컬 달력일 버킷**으로 묶고 정렬·검색·종류 필터를
 * 적용하는 **순수 함수** 모음이다.
 *
 * 순수성 원칙 — i18n·React·Date.now() 에 의존하지 않는다(현재 시각은 인자로 주입). 라벨 문자열이 아니라
 * `DayLabelParts` 라는 **데이터**를 돌려주고, 문구 조립은 컴포넌트가 기존 `chat.list.day.*` 키로 한다.
 * 덕분에 `MessageList.tsx` 의 모듈 로컬 `formatDayLabel` 을 건드리지 않아도 라벨이 채팅 본문의
 * 날짜 구분선과 동일하게 나온다.
 *
 * ⚠ 첨부 메타는 보장되지 않는다 — 서버는 클라이언트가 보낸 jsonb 를 verbatim 으로 되돌려주므로
 * `fileId` 만 확실하고 `name`/`mimeType`/`sizeBytes`/`createdAt` 은 없을 수 있다. 모든 함수는 누락에
 * 안전해야 하고, 날짜가 없는 첨부도 **버리지 않고** '날짜 미상' 버킷으로 보낸다.
 */

/** 미디어 탭 — 이미지/파일. (후속에서 'links' 확장 여지) */
export type MediaTab = 'files' | 'images';

/** 정렬 기준 축. 방향(MediaSortDir)과 직교. */
export type MediaSortKey = 'date' | 'name' | 'size';
export type MediaSortDir = 'desc' | 'asc';

/** 파일 탭 종류 필터. 'all' 은 필터 없음. */
export type MediaKind = 'all' | 'doc' | 'sheet' | 'archive' | 'etc';

/** 종류 칩 렌더 순서 SSOT. */
export const MEDIA_KINDS: readonly MediaKind[] = ['all', 'doc', 'sheet', 'archive', 'etc'];
/** 정렬 버튼 렌더 순서 SSOT. */
export const MEDIA_SORT_KEYS: readonly MediaSortKey[] = ['date', 'name', 'size'];

/** 날짜 미상 버킷의 dayKey. 항상 목록 맨 뒤에 배치된다. */
export const UNKNOWN_DAY_KEY = '';

export interface AttachmentDayGroup {
  /** 'YYYY-MM-DD' 로컬 달력일. UNKNOWN_DAY_KEY('') 면 날짜 미상. */
  dayKey: string;
  /** 라벨 포맷용 대표 ISO(그룹 첫 항목). 날짜 미상이면 ''. */
  isoAnchor: string;
  items: DatedAttachment[];
  totalBytes: number;
}

export type DayLabelKind = 'today' | 'yesterday' | 'md' | 'ymd' | 'unknown';

export interface DayLabelParts {
  kind: DayLabelKind;
  year: number;
  month: number;
  day: number;
}

export interface BuildDayGroupsOptions {
  query?: string;
  sortKey?: MediaSortKey;
  sortDir?: MediaSortDir;
  kind?: MediaKind;
  /** senderId → 표시 이름. 검색이 발신자 이름도 매칭하게 해준다(보낸사람 필터 대용). */
  senderNameById?: Record<string, string>;
}

/**
 * ISO → 로컬 달력일 키 'YYYY-MM-DD'.
 *
 * ⚠ `toISOString().slice(0,10)` 를 쓰면 안 된다 — UTC 기준이라 KST 저녁(예: 21시)에 보낸 파일이
 * 다음 날 그룹으로 밀린다. 반드시 로컬 getFullYear/getMonth/getDate 를 쓴다.
 * 사전순 정렬 = 시간순이 되도록 제로 패딩한다.
 */
export function localDayKey(iso: string | null | undefined): string {
  if (!iso) return UNKNOWN_DAY_KEY;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return UNKNOWN_DAY_KEY;
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * dayKey → 라벨 조립용 데이터. 오늘/어제/같은 해(M월 D일)/다른 해(Y년 M월 D일)/날짜 미상.
 *
 * @param nowMs 현재 시각(ms). 테스트에서 고정하기 위해 주입 가능 — 미지정 시 실제 현재 시각.
 */
export function dayLabelParts(dayKey: string, nowMs?: number): DayLabelParts {
  if (!dayKey) return { kind: 'unknown', year: 0, month: 0, day: 0 };
  const [ys, ms, ds] = dayKey.split('-');
  const year = Number(ys);
  const month = Number(ms);
  const day = Number(ds);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return { kind: 'unknown', year: 0, month: 0, day: 0 };
  }

  const now = nowMs === undefined ? new Date() : new Date(nowMs);
  const todayKey = localDayKey(now.toISOString());
  if (dayKey === todayKey) return { kind: 'today', year, month, day };

  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (dayKey === localDayKey(yesterday.toISOString())) return { kind: 'yesterday', year, month, day };

  return { kind: year === now.getFullYear() ? 'md' : 'ymd', year, month, day };
}

/**
 * 종류 칩 분류. `lib/file-icon.ts` 의 `fileCategory()` SSOT 에 위임해 아이콘 색상과 분류가 어긋나지 않게 한다.
 * 문서(pdf/word/ppt/md/html) · 표(excel) · 압축 · 기타(이미지·미분류).
 */
export function mediaKindOf(a: { name?: string | null; mimeType?: string | null }): Exclude<MediaKind, 'all'> {
  switch (fileCategory(a)) {
    case 'pdf':
    case 'word':
    case 'ppt':
    case 'md':
    case 'html':
      return 'doc';
    case 'excel':
      return 'sheet';
    case 'archive':
      return 'archive';
    default:
      return 'etc';
  }
}

/**
 * 검색어를 토큰으로 분해. 공백 분할 + 소문자 + NFC 정규화(한글 자소 분리 파일명 대응).
 * 빈 문자열이면 빈 배열 → 필터 미적용 신호.
 */
export function queryTokens(query: string | undefined): string[] {
  if (!query) return [];
  return query
    .normalize('NFC')
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length > 0);
}

/** 검색 대상 문자열 — 파일명(없으면 fileId 폴백) + 발신자 이름. */
function haystack(a: DatedAttachment, senderNameById?: Record<string, string>): string {
  const name = a.name ?? `#${a.fileId}`;
  const sender = a.senderId ? (senderNameById?.[a.senderId] ?? '') : '';
  return `${name} ${sender}`.normalize('NFC').toLowerCase();
}

/** 모든 토큰이 포함돼야 매칭(AND). 토큰이 없으면 항상 true. */
export function matchesQuery(
  a: DatedAttachment,
  tokens: readonly string[],
  senderNameById?: Record<string, string>,
): boolean {
  if (tokens.length === 0) return true;
  const hay = haystack(a, senderNameById);
  return tokens.every((t) => hay.includes(t));
}

/**
 * 검색어 하이라이트용 분할 — `dangerouslySetInnerHTML` 없이 `<mark>` 를 그릴 수 있게
 * `{ t, hit }` 조각 배열로 쪼갠다. 토큰이 겹치면 합쳐서 한 조각으로 낸다.
 */
export function splitHighlight(text: string, tokens: readonly string[]): Array<{ t: string; hit: boolean }> {
  if (!text) return [];
  if (tokens.length === 0) return [{ t: text, hit: false }];

  const lower = text.toLowerCase();
  // 매칭 구간을 모두 모아 병합 → 조각화. (토큰 수가 적어 O(n·m) 로 충분)
  const ranges: Array<[number, number]> = [];
  for (const tok of tokens) {
    if (!tok) continue;
    let from = 0;
    for (;;) {
      const i = lower.indexOf(tok, from);
      if (i < 0) break;
      ranges.push([i, i + tok.length]);
      from = i + tok.length;
    }
  }
  if (ranges.length === 0) return [{ t: text, hit: false }];

  ranges.sort((x, y) => x[0] - y[0]);
  const merged: Array<[number, number]> = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
    else merged.push([r[0], r[1]]);
  }

  const out: Array<{ t: string; hit: boolean }> = [];
  let cursor = 0;
  for (const [s, e] of merged) {
    if (s > cursor) out.push({ t: text.slice(cursor, s), hit: false });
    out.push({ t: text.slice(s, e), hit: true });
    cursor = e;
  }
  if (cursor < text.length) out.push({ t: text.slice(cursor), hit: false });
  return out;
}

/** 그룹 총 용량. sizeBytes 누락은 0 취급. */
export function totalBytesOf(items: readonly DatedAttachment[]): number {
  let sum = 0;
  for (const a of items) sum += a.sizeBytes ?? 0;
  return sum;
}

/** 표시 이름 — 파일명이 없는 레거시 첨부는 '#<fileId>' 로 폴백(검색 haystack 과 동일 규칙). */
export function displayNameOf(a: DatedAttachment): string {
  return a.name ?? `#${a.fileId}`;
}

function compareByName(a: DatedAttachment, b: DatedAttachment): number {
  return displayNameOf(a).localeCompare(displayNameOf(b), undefined, { numeric: true, sensitivity: 'base' });
}

function compareBySize(a: DatedAttachment, b: DatedAttachment): number {
  return (a.sizeBytes ?? 0) - (b.sizeBytes ?? 0);
}

/**
 * 필터 → 일자 그룹 → 정렬.
 *
 * **정렬 의미론(의도적):**
 *   - `date`  : 그룹 순서와 그룹 내부 순서를 함께 뒤집는다.
 *   - `name` / `size` : **그룹 내부에만** 적용하고 그룹 순서는 최신순을 유지한다.
 *     이름·크기로 전체를 평면 정렬하면 사용자가 요청한 일자 아코디언 구조가 무너지기 때문이다.
 *
 * 입력 `items` 는 `mergeRoomAttachmentsDated` 가 이미 최신순으로 정렬해 준 배열을 가정한다
 * (그룹 내부 date 정렬은 그 순서를 그대로 쓰거나 뒤집기만 한다 — bigint 문자열 재비교 불필요).
 */
export function buildDayGroups(
  items: readonly DatedAttachment[],
  opts: BuildDayGroupsOptions = {},
): AttachmentDayGroup[] {
  const { sortKey = 'date', sortDir = 'desc', kind = 'all', senderNameById } = opts;
  const tokens = queryTokens(opts.query);

  const byDay = new Map<string, DatedAttachment[]>();
  for (const a of items) {
    if (kind !== 'all' && mediaKindOf(a) !== kind) continue;
    if (!matchesQuery(a, tokens, senderNameById)) continue;
    const key = localDayKey(a.createdAt);
    const bucket = byDay.get(key);
    if (bucket) bucket.push(a);
    else byDay.set(key, [a]);
  }

  const groupsAsc = sortKey === 'date' && sortDir === 'asc';
  const keys = [...byDay.keys()].sort((x, y) => {
    // 날짜 미상은 정렬 방향과 무관하게 항상 맨 뒤.
    if (x === UNKNOWN_DAY_KEY) return 1;
    if (y === UNKNOWN_DAY_KEY) return -1;
    return groupsAsc ? x.localeCompare(y) : y.localeCompare(x);
  });

  return keys.map((dayKey) => {
    const bucket = byDay.get(dayKey)!;
    let sorted: DatedAttachment[];
    if (sortKey === 'name') {
      sorted = [...bucket].sort((a, b) => (sortDir === 'asc' ? compareByName(a, b) : compareByName(b, a)));
    } else if (sortKey === 'size') {
      sorted = [...bucket].sort((a, b) => (sortDir === 'asc' ? compareBySize(a, b) : compareBySize(b, a)));
    } else {
      // 입력이 이미 최신순 — asc 면 뒤집기만.
      sorted = sortDir === 'asc' ? [...bucket].reverse() : bucket;
    }
    return {
      dayKey,
      isoAnchor: sorted[0]?.createdAt ?? '',
      items: sorted,
      totalBytes: totalBytesOf(sorted),
    };
  });
}

/** 초기 펼침 대상 — 앞에서부터 `count` 개 그룹. 기본 1개(가장 최근 일자만). */
export function defaultExpandedKeys(groups: readonly AttachmentDayGroup[], count = 1): string[] {
  return groups.slice(0, Math.max(0, count)).map((g) => g.dayKey);
}
